// ABOUTME: React hook for real-time subscription status polling with smart interval management
// ABOUTME: Supports aggressive polling during checkout/payment recovery, tab visibility detection, and exponential backoff

import { useState, useEffect, useRef, useCallback } from 'react';
import { SubscriptionStatusResponse } from '../types/api';
import { subscriptionAPI } from '../services/api';

const NORMAL_POLL_INTERVAL = 30000; // 30 seconds
const AGGRESSIVE_POLL_INTERVAL = 5000; // 5 seconds
const MAX_BACKOFF_INTERVAL = 300000; // 5 minutes (Issue #1150)
const BACKOFF_MULTIPLIER = 1.5; // Issue #1150

interface UseSubscriptionStatusOptions {
  /**
   * When true, polls every 5 seconds. When false, polls every 30 seconds.
   * Set to true when checkout is open or payment has failed.
   */
  aggressivePolling?: boolean;

  /**
   * Whether polling should be active at all.
   * Set to false to stop polling (e.g., when leaving MyFamilyPage).
   */
  enabled?: boolean;

  /**
   * Callback invoked when subscription status changes.
   * Useful for triggering UI updates when status becomes 'active'.
   */
  onStatusChange?: (newStatus: SubscriptionStatusResponse) => void;
}

interface UseSubscriptionStatusReturn {
  subscriptionStatus: SubscriptionStatusResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for polling subscription status with smart interval management.
 *
 * Polling behavior:
 * - Polls at 5-second intervals when aggressivePolling is true
 * - Polls at 30-second intervals when aggressivePolling is false
 * - Pauses polling when browser tab is hidden (document.hidden)
 * - Resumes polling when tab becomes visible again
 * - Stops polling when enabled is false
 * - Automatically cleans up on unmount
 *
 * @param options - Configuration for polling behavior
 * @returns Subscription status, loading state, error state, and refetch function
 */
export function useSubscriptionStatus(
  options: UseSubscriptionStatusOptions = {}
): UseSubscriptionStatusReturn {
  const { aggressivePolling = false, enabled = true, onStatusChange } = options;

  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Issue #1150: Add state for exponential backoff
  const [currentInterval, setCurrentInterval] = useState<number>(NORMAL_POLL_INTERVAL);
  const [consecutiveFailures, setConsecutiveFailures] = useState<number>(0);

  // Use refs to track the latest values without causing effect re-runs
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousStatusRef = useRef<string | null>(null);
  const onStatusChangeRef = useRef(onStatusChange);

  // Update callback ref when it changes
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  /**
   * Fetches the current subscription status from the API.
   * Issue #1150: Use centralized API service with exponential backoff on failures
   */
  const fetchStatus = useCallback(async () => {
    try {
      const data = await subscriptionAPI.getSubscriptionStatus();

      // Success - reset backoff (Issue #1150)
      setConsecutiveFailures(0);
      setCurrentInterval(aggressivePolling ? AGGRESSIVE_POLL_INTERVAL : NORMAL_POLL_INTERVAL);

      // Check if status changed and invoke callback
      if (previousStatusRef.current !== data.status) {
        previousStatusRef.current = data.status;

        if (onStatusChangeRef.current) {
          onStatusChangeRef.current(data);
        }
      }

      setSubscriptionStatus(data);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching subscription status:', err);

      // Issue #1150: Increment failure count and apply exponential backoff
      const newFailureCount = consecutiveFailures + 1;
      setConsecutiveFailures(newFailureCount);

      const baseInterval = aggressivePolling ? AGGRESSIVE_POLL_INTERVAL : NORMAL_POLL_INTERVAL;
      const backoffInterval = Math.min(
        baseInterval * Math.pow(BACKOFF_MULTIPLIER, newFailureCount),
        MAX_BACKOFF_INTERVAL
      );
      setCurrentInterval(backoffInterval);

      setError(err instanceof Error ? err.message : 'Failed to fetch subscription status');
      setLoading(false);
    }
  }, [aggressivePolling, consecutiveFailures]);

  /**
   * Public refetch function for manual refresh.
   */
  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchStatus();
  }, [fetchStatus]);

  /**
   * Starts polling at the appropriate interval.
   * Issue #1150: Use currentInterval for backoff support
   */
  const startPolling = useCallback(() => {
    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Don't start polling if not enabled or if document is hidden
    if (!enabled || document.hidden) {
      return;
    }

    // Fetch immediately
    fetchStatus();

    // Issue #1150: Use currentInterval which includes backoff
    pollIntervalRef.current = setInterval(() => {
      // Double-check visibility before each poll
      if (!document.hidden) {
        fetchStatus();
      }
    }, currentInterval);
  }, [enabled, currentInterval, fetchStatus]);

  /**
   * Stops polling.
   */
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  /**
   * Handle visibility changes - pause polling when tab hidden, resume when visible.
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else if (enabled) {
        startPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, startPolling, stopPolling]);

  /**
   * Main effect - start/stop polling based on enabled state and interval changes.
   * Issue #1150: Include currentInterval in dependencies to restart polling when backoff changes
   */
  useEffect(() => {
    if (enabled && !document.hidden) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- startPolling/stopPolling manage setInterval, not direct setState; polling callbacks call setState asynchronously
      startPolling();
    } else {
      stopPolling();
    }

    // Cleanup on unmount or when enabled changes to false
    return () => {
      stopPolling();
    };
  }, [enabled, aggressivePolling, currentInterval, startPolling, stopPolling]);

  return {
    subscriptionStatus,
    loading,
    error,
    refetch
  };
}
